<template>
    <page
        :id="state_identifier"
        title="Project Requests"
        :status="page_status"
        :waiting="waiting"
        @statused="statused"
    >
        <template v-slot:title><span></span></template>

        <div class="uk-margin-large-top">
            <data-table
                id="project_requests"
                :columns="visible_columns"
                :rows="filtered_rows"
                :search="state.search"
                :total="state.total"
                :found="state.found"
                :current-page="state.page"
                :total-pages="nb_pages"
                :searchable="false"
                :can-add="true"
                table-class="lam-records-list"
                empty-title="No project requests found"
                empty-add-button-title="Add the first project request..."
                @sort="sort_by"
                @search="search"
                @page="goto_page"
                @click="record_edit(entity, $event.id)"
                @add="get_new_project_request_name"
            >
                <template v-slot:controls>
                    <form v-if="!!champions.length" @submit.prevent.stop class="uk-form">
                        <select @change="filter('champion_id', $event.target.value)">
                            <option value="" :selected="!state.filter">(Any champion)</option>
                            <option
                                v-for="champion in champions"
                                :key="champion.id"
                                :value="champion.id"
                                :selected="state.filter === champion.id"
                            >
                                {{ champion.initials }}
                            </option>
                        </select>
                    </form>
                </template>
                <template
                    v-slot:summary="{ row }"
                >
                    <h6>{{ row.name }}</h6>
                </template>
                <template
                    v-slot:tbody="{ row, field }"
                >
                    <span v-if="field === 'champion_id'">{{ user_by_id(row.champion_id).initials }}</span>
                    <span v-else-if="field === 'firm_id'">{{ firm_by_id(row.firm_id).name }}</span>
                    <div v-else-if="field === 'size'" class="uk-flex">
                        <span v-if="!!row.size">
                            {{ row.size | nibnut.number("0,0") }}
                            <span v-if="row.size_units === 'AC'">Acres</span>
                            <span v-else-if="row.size_units === 'SM'">M<sup>2</sup></span>
                            <span v-else-if="row.size_units === 'SF'">Ft<sup>2</sup></span>
                        </span>
                    </div>
                    <span v-else-if="field === 'category_ids'">{{ categories_by_ids(row.category_ids) }}</span>
                    <span v-else-if="field === 'confidential'">{{ row.confidential ? 'Yes' : 'No' }}</span>
                    <span v-else-if="field === 'additional_info'">{{ request_types_by_ids(row.additional_info.requests_for_materials).join(", ") }}</span>
                    <span v-else-if="field.match(/^(due|starts)_at$/i)">{{ row[field] | nibnut.date("MM/DD/YY") }}</span>
                    <div v-else-if="field === '_buttons'">
                        <base-button
                            v-if="!record_is_draft(row)"
                            size="small"
                            color="secondary"
                            @click.native.prevent.stop="copy_record(row)"
                        >
                            <open-icon glyph="clipboard" />
                        </base-button>
                    </div>
                    <span v-else>{{ row[field] }}</span>
                </template>
            </data-table>
        </div>

        <modal-dialog
            id="new-project-request-name-dialog"
            :show.sync="getting_new_project_request_name"
        >
            <template v-slot:title>
                New Project Request
            </template>

            <div class="uk-form-controls-text">Please provide the project's name:</div>
            <form-input
                id="new-project-request-name"
                name="new_project_request_name"
                v-model="new_project_request_name"
                :editable="true"
                :required="true"
                class="uk-width-1-1"
            />

            <div class="uk-modal-footer uk-text-center">
                <div class="uk-grid">
                    <div class="uk-width-1-2">
                        <base-button
                            size="large"
                            width="1-1"
                            :disabled="creating_new_project_request"
                            @click.prevent="getting_new_project_request_name = false"
                            class="uk-width-1-1"
                        >
                            Cancel
                        </base-button>
                    </div>
                    <div class="uk-width-1-2">
                        <base-button
                            :waiting="creating_new_project_request"
                            :disabled="creating_new_project_request"
                            color="primary"
                            size="large"
                            width="1-1"
                            @click.prevent="create_new_project_request"
                            class="uk-width-1-1"
                        >
                            Create
                        </base-button>
                    </div>
                </div>
            </div>
        </modal-dialog>
    </page>
</template>

<script type="text/javascript">
import orderBy from "lodash/orderBy"

import is_records_list_page from "../../mixins/IsRecordsListPage"
import handles_project_requests from "./HandlesProjectRequests"

import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import FormInput from "../../ui/FormInput"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"

export default {
    mixins: [is_records_list_page, handles_project_requests],
    components: {
        ModalDialog,
        FormInput,
        BaseButton,
        OpenIcon
    },
    mounted () {
        this.$store.dispatch("FETCH_RECORDS", { entity: "category" })
    },
    methods: {
        statused (status) {
            if(!this.can("project_request.view")) status = 401
            this.page_status = status
            this.waiting = false
        },
        get_new_project_request_name () {
            this.new_project_request_name = ""
            this.getting_new_project_request_name = true
        },
        create_new_project_request () {
            if(!this.new_project_request_name) this.$error("Please enter a project name.")
            else {
                this.creating_new_project_request = true
                this.$store.dispatch("CREATE_RECORD", {
                    entity: this.entity,
                    data: { name: this.new_project_request_name }
                }).then(record => {
                    this.getting_new_project_request_name = false
                    this.$router.push({ name: "project_request.edit", params: { id: record.id } })
                }).catch(error => {
                    this.$error(error.message)
                }).finally(() => {
                    this.creating_new_project_request = false
                })
            }
        },
        filter_rows (rows) {
            return rows
        },
        search_rows (rows) {
            return rows
        },
        user_by_id (user_id) {
            return this.entity_record("user", user_id) || { initials: user_id }
        },
        firm_by_id (firm_id) {
            return this.entity_record("firm", firm_id) || { name: "" }
        },
        categories_by_ids (category_ids) {
            return category_ids.map(category_id => {
                const category = this.entity_record("category", category_id)
                return category ? category.name : ""
            }).filter(name => !!name).join(", ")
        },
        request_types_by_ids (type_ids) {
            if(!type_ids) return []
            this.dictionary("request_for_material")
            return type_ids.map(type_id => {
                const type_name = this.term_value_by(type_id, "request_for_material", "id")
                return type_name ? type_name[0] : type_id
            })
        }
    },
    computed: {
        fields () {
            const fields = Object.keys(this.columns)
            fields.push("size_units")
            fields.push("other_recipient_ids")
            return fields
        },
        champions () {
            return orderBy(this.entity_records("user"), "initials", "asc")
        }
    },
    data () {
        return {
            entity: "project_request",
            relation_ids: ["address", "firm"],
            columns: {
                champion_id: { label: "Champion", sort: null, type: "plain" },
                name: { label: "Project Name", sort: "asc", type: "plain", cell_class: "lam-col-name" },
                physical_address_short_label: { label: "Location", sort: false, cell_class: { "uk-text-nowrap": true } },
                firm_id: { label: "Client", sort: false },
                size: { label: "Project Size", sort: null },
                category_ids: { label: "Project Type", sort: false },
                confidential: { label: "Confidential", sort: null },
                estimated_fee: { label: "Estimated Fee", sort: null },
                additional_info: { label: "RFMs", sort: false, cell_class: { "uk-text-nowrap": true } },
                due_at: { label: "Content Due by", sort: null },
                starts_at: { label: "Approx. Lam Start Date", sort: null }
                // _buttons: { label: " ", sort: false }
            },
            default_state: {
                per_page: 30,
                page: 1,
                sort_by: "name",
                sort_dir: "asc",
                filter_on: "champion_id",
                filter: 0,
                archived: false,
                search: "",
                total: 0,
                found: 0
            },

            new_project_request_name: "",
            getting_new_project_request_name: false,
            creating_new_project_request: false
        }
    }
}
</script>
